import { useScrollReveal } from "@/hooks/useScrollReveal";

const CASE_STAGES = [
  { stage: "Detección", description: "Registro inicial del incidente o situación", icon: "ri-radar-line", color: "bg-primary-500", stats: "34 casos", avg: "Inmediato" },
  { stage: "Evaluación", description: "Análisis de antecedentes y activación de protocolo", icon: "ri-search-eye-line", color: "bg-accent-500", stats: "28 en curso", avg: "24-48h" },
  { stage: "Intervención", description: "Entrevistas, medidas y seguimiento del caso", icon: "ri-heart-pulse-line", color: "bg-secondary-500", stats: "22 activos", avg: "1-4 semanas" },
  { stage: "Resolución", description: "Cierre del caso con acuerdos y medidas", icon: "ri-check-double-line", color: "bg-emerald-500", stats: "19 este mes", avg: "Variable" },
  { stage: "Seguimiento", description: "Monitoreo post-cierre para prevenir reincidencia", icon: "ri-line-chart-line", color: "bg-primary-500", stats: "15 en monitoreo", avg: "3-6 meses" },
];

const RESOLUTION_DATA = [
  { category: "Casos de convivencia", opened: 45, resolved: 38, color: "bg-primary-500" },
  { category: "Casos de orientación", opened: 22, resolved: 19, color: "bg-accent-500" },
  { category: "Casos psicológicos", opened: 18, resolved: 15, color: "bg-secondary-500" },
  { category: "Requerimientos judiciales", opened: 12, resolved: 8, color: "bg-amber-500" },
  { category: "Derivaciones externas", opened: 9, resolved: 7, color: "bg-foreground-500" },
];

const ALERTS_TRACKING = [
  { text: "3 casos sin acción en +72 horas requieren intervención inmediata", type: "critical", icon: "ri-error-warning-line" },
  { text: "5 informes para tribunal deben entregarse este mes", type: "warning", icon: "ri-file-warning-line" },
  { text: "2 casos en seguimiento post-cierre próximos a revisión", type: "info", icon: "ri-information-line" },
];

export default function GestionCasosSection() {
  const sectionRef = useScrollReveal<HTMLElement>({ threshold: 0.12, selector: ".scroll-reveal", staggerDelay: 100 });

  return (
    <section id="gestion-casos" className="py-20 md:py-28 bg-background-50 relative overflow-hidden">
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-accent-50/40 blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-16">
          <span className="scroll-reveal text-xs font-semibold text-secondary-600 tracking-widest uppercase bg-secondary-50 px-3 py-1 rounded-full inline-block">
            Gestión de Casos
          </span>
          <h2 className="scroll-reveal text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mt-4 tracking-tight">
            Ciclo de vida completo de cada caso
          </h2>
          <p className="scroll-reveal text-lg text-foreground-500 mt-4 max-w-2xl mx-auto leading-relaxed">
            Desde la detección hasta el seguimiento post-cierre. Cada etapa queda registrada con responsables, fechas y evidencias para trazabilidad total.
          </p>
        </div>

        {/* Case lifecycle stages */}
        <div className="scroll-reveal mb-8">
          <div className="bg-white rounded-3xl border border-background-200/80 shadow-soft-lg p-6 md:p-8 overflow-hidden">
            <h3 className="text-sm font-bold text-foreground-800 mb-6 text-center uppercase tracking-wider">Ciclo de vida del caso</h3>
            <div className="flex items-start justify-center flex-wrap gap-2 md:gap-3">
              {CASE_STAGES.map((stage, i) => (
                <div key={stage.stage} className="flex items-start gap-2 md:gap-3">
                  <div className="flex flex-col items-center gap-3 max-w-[140px] text-center">
                    <div className={`w-12 h-12 md:w-14 md:h-14 rounded-2xl ${stage.color} flex items-center justify-center shadow-glow-primary hover:scale-110 transition-transform duration-300`}>
                      <i className={`${stage.icon} text-white text-lg md:text-xl`} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground-900">{stage.stage}</p>
                      <p className="text-[11px] text-foreground-500 mt-0.5 leading-tight">{stage.description}</p>
                      <div className="flex items-center justify-center gap-2 mt-1.5">
                        <span className="text-[10px] font-semibold text-foreground-700 bg-background-100 px-1.5 py-0.5 rounded">{stage.stats}</span>
                        <span className="text-[10px] text-foreground-400">{stage.avg}</span>
                      </div>
                    </div>
                  </div>
                  {i < CASE_STAGES.length - 1 && (
                    <div className="flex items-center pt-6">
                      <div className="h-0.5 w-6 md:w-10 bg-gradient-to-r from-primary-300 to-accent-300 rounded-full" />
                      <i className="ri-arrow-right-s-line text-foreground-300 text-xs" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Resolution stats + Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Resolution bars */}
          <div className="lg:col-span-2 scroll-reveal">
            <div className="bg-white rounded-3xl border border-background-200/80 shadow-soft-lg p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-foreground-950">Resolución por categoría</h3>
                <span className="text-xs text-foreground-400">Último trimestre</span>
              </div>

              <div className="space-y-5">
                {RESOLUTION_DATA.map((item, i) => (
                  <div key={item.category}>
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-2.5 h-2.5 rounded-full ${item.color}`} />
                        <span className="text-sm font-semibold text-foreground-800">{item.category}</span>
                      </div>
                      <span className="text-sm text-foreground-500 font-medium">
                        {item.resolved}/{item.opened} <span className="text-foreground-300">resueltos</span>
                      </span>
                    </div>
                    <div className="relative h-3 bg-background-200 rounded-full overflow-hidden">
                      <div
                        className={`absolute inset-y-0 left-0 ${item.color} rounded-full animate-kpi-bar`}
                        style={{ width: `${Math.round((item.resolved / item.opened) * 100)}%`, animationDelay: `${i * 150}ms` }}
                      />
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-foreground-400">{Math.round((item.resolved / item.opened) * 100)}% resolución</span>
                      <span className="text-xs text-foreground-300">{item.opened - item.resolved} pendientes</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-4 border-t border-background-200/60 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-secondary-500" />
                  <span className="text-xs text-foreground-500">Tasa global de resolución: 82%</span>
                </div>
                <button className="text-xs font-semibold text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap flex items-center gap-1">
                  Ver detalle por área <i className="ri-arrow-right-line" />
                </button>
              </div>
            </div>
          </div>

          {/* Alerts panel */}
          <div className="scroll-reveal bg-white rounded-2xl border border-background-200/80 shadow-soft p-6">
            <div className="flex items-center gap-2 mb-4">
              <i className="ri-notification-3-line text-accent-500" />
              <h4 className="text-sm font-bold text-foreground-800 uppercase tracking-wider">Alertas de trazabilidad</h4>
            </div>
            <div className="space-y-3">
              {ALERTS_TRACKING.map((alert, i) => (
                <div key={i} className={`flex items-start gap-3 p-3 rounded-xl ${
                  alert.type === "critical" ? "bg-red-50" : alert.type === "warning" ? "bg-amber-50" : "bg-primary-50"
                }`}>
                  <i className={`${alert.icon} text-sm flex-shrink-0 mt-0.5 ${
                    alert.type === "critical" ? "text-red-500" : alert.type === "warning" ? "text-amber-500" : "text-primary-500"
                  }`} />
                  <p className="text-xs text-foreground-700 leading-relaxed">{alert.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}